/**
 * Mind Coach page — 7 pre-configured cognitive coaches.
 *
 * Owner brief Feb 2026: "I need this building in to my app" — set of
 * billionaire-thinker / neuro-tutor / life-architect etc. prompts.
 *
 * UX: landing card grid → click a coach → dedicated chat panel with
 * that persona's locked system prompt (defined server-side in
 * /app/backend/routes/coach.py so the prompt never leaks to client).
 *
 * Each coach has its OWN persistent thread — you can bounce between
 * coaches without losing context.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

const T = {
  bg: "#050b18",
  panel: "rgba(7,17,33,0.72)",
  border: "rgba(34,211,238,0.24)",
  text: "#e6f3ff",
  textDim: "#7897b8",
  cyan: "#22d3ee",
};

export default function Coach() {
  const nav = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [active, setActive] = useState(null); // persona object once selected
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  // Subscription state — { [persona_id]: {hhmm, active} }
  const [subs, setSubs] = useState({});
  // Today's digest — proactive nudges from subscribed coaches
  const [digest, setDigest] = useState([]);
  const scrollRef = useRef(null);

  // Fetch persona catalogue + current subscriptions + today's digest.
  useEffect(() => {
    (async () => {
      try {
        const [r1, r2, r3] = await Promise.all([
          api.get("/coach/personas"),
          api.get("/coach/subscriptions").catch(() => ({ data: { subscriptions: [] } })),
          api.get("/coach/digest").catch(() => ({ data: { digest: [] } })),
        ]);
        setPersonas(r1.data?.personas || []);
        const subMap = {};
        (r2.data?.subscriptions || []).forEach(s => { subMap[s.persona_id] = s; });
        setSubs(subMap);
        setDigest(r3.data?.digest || []);
      } catch (e) {
        console.warn("[coach] boot fetch failed:", e?.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleSubscribe = async (personaId, e) => {
    // stop the card-click from also selecting the persona
    e?.stopPropagation?.();
    const isSubbed = !!subs[personaId]?.active;
    try {
      if (isSubbed) {
        await api.post("/coach/unsubscribe", { persona_id: personaId });
        setSubs(prev => ({ ...prev, [personaId]: { ...(prev[personaId] || {}), active: false } }));
      } else {
        const r = await api.post("/coach/subscribe", { persona_id: personaId, hhmm: "06:00" });
        setSubs(prev => ({ ...prev, [personaId]: { persona_id: personaId, hhmm: r.data?.hhmm || "06:00", active: true } }));
      }
    } catch (err) {
      console.warn("[coach] subscribe toggle failed:", err?.message);
    }
  };

  // Load thread history when a persona is selected.
  useEffect(() => {
    if (!active) return;
    (async () => {
      try {
        const r = await api.get(`/coach/thread/${active.id}`);
        setMessages(r.data?.messages || []);
      } catch { setMessages([]); }
    })();
  }, [active]);

  // Autoscroll on new messages.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy || !active) return;
    setBusy(true);
    setInput("");
    // Optimistic user echo
    setMessages(prev => [...prev, { role: "user", content: msg, ts: new Date().toISOString() }]);
    try {
      const r = await api.post("/coach/chat", { persona_id: active.id, message: msg });
      setMessages(r.data?.messages || []);
    } catch (e) {
      setMessages(prev => [...prev, { role: "coach", content: `⚠️ Coach unavailable — ${e?.message || "unknown error"}`, ts: new Date().toISOString() }]);
    } finally { setBusy(false); }
  };

  const wipeThread = async () => {
    if (!active) return;
    if (!window.confirm(`Wipe your ${active.name} chat history?`)) return;
    try {
      await api.delete(`/coach/thread/${active.id}`);
      setMessages([]);
    } catch { /* ignore */ }
  };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text, fontFamily: "ui-sans-serif, system-ui" }}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4"
           style={{ borderBottom: `1px solid ${T.border}` }}>
        <div>
          <div className="text-xs uppercase tracking-[0.3em]" style={{ color: T.cyan }}>Mind Coach</div>
          <div className="text-lg font-semibold" style={{ color: T.text }}>
            {active ? `${active.emoji} ${active.name}` : "Choose your coach"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {active && (
            <>
              <button
                data-testid="coach-back"
                onClick={() => { setActive(null); setMessages([]); }}
                className="text-xs px-3 py-1.5"
                style={{ background: "transparent", border: `1px solid ${T.border}`, color: T.textDim }}
              >← All coaches</button>
              <button
                data-testid="coach-wipe-thread"
                onClick={wipeThread}
                className="text-xs px-3 py-1.5"
                style={{ background: "transparent", border: `1px solid ${T.border}`, color: T.textDim }}
              >Wipe history</button>
            </>
          )}
          <button
            data-testid="coach-close"
            onClick={() => nav("/")}
            className="text-xs px-3 py-1.5"
            style={{ background: T.cyan, color: T.bg, fontWeight: 700 }}
          >Close</button>
        </div>
      </div>

      {loading && (
        <div className="text-center py-16" style={{ color: T.textDim }}>Loading coaches…</div>
      )}

      {!loading && !active && (
        <div className="max-w-6xl mx-auto p-6">
          {/* Today's Digest — proactive nudges from subscribed coaches */}
          {digest.length > 0 && (
            <div className="mb-6" data-testid="coach-digest">
              <div className="text-xs uppercase tracking-widest mb-2" style={{ color: T.textDim }}>
                Today&apos;s nudges
              </div>
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
                {digest.slice(0, 4).map((d, i) => (
                  <div key={d.id || i}
                       className="p-4"
                       data-testid={`coach-digest-${d.persona_id}`}
                       style={{
                         background: "rgba(7,17,33,0.72)",
                         border: `1px solid ${T.border}`,
                         borderRadius: 8,
                       }}>
                    <div className="text-xs mb-1" style={{ color: T.cyan }}>
                      {d.persona_emoji} {d.persona_name}
                    </div>
                    <div className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: T.text }}>
                      {d.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-4"
               style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}
               data-testid="coach-catalogue">
            {personas.map(p => {
              const subbed = !!subs[p.id]?.active;
              return (
                <div key={p.id} className="relative">
                  <button
                    data-testid={`coach-card-${p.id}`}
                    onClick={() => setActive(p)}
                    className="w-full text-left p-5 transition-transform hover:scale-[1.02]"
                    style={{
                      background: `linear-gradient(135deg, ${p.gradient?.[0] || T.cyan}22, ${p.gradient?.[1] || "#8b5cf6"}22)`,
                      border: `1px solid ${p.gradient?.[0] || T.cyan}44`,
                      borderRadius: 8,
                      color: T.text,
                      cursor: "pointer",
                    }}
                  >
                    <div className="text-3xl mb-2">{p.emoji}</div>
                    <div className="text-base font-bold mb-1" style={{ color: p.gradient?.[0] || T.cyan }}>{p.name}</div>
                    <div className="text-xs leading-relaxed mb-4" style={{ color: T.textDim }}>{p.tagline}</div>
                  </button>
                  {/* Subscribe pill — sits over the bottom of the card */}
                  <button
                    data-testid={`coach-sub-${p.id}`}
                    onClick={(e) => toggleSubscribe(p.id, e)}
                    className="absolute right-3 bottom-3 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 transition-colors"
                    style={{
                      background: subbed ? (p.gradient?.[0] || T.cyan) : "transparent",
                      border: `1px solid ${p.gradient?.[0] || T.cyan}`,
                      color: subbed ? T.bg : (p.gradient?.[0] || T.cyan),
                      borderRadius: 999,
                      cursor: "pointer",
                    }}
                    title={subbed
                      ? `Subscribed — daily prompt at ${subs[p.id]?.hhmm || "06:00"} UK time. Click to unsubscribe.`
                      : "Get a daily prompt from this coach at 06:00 UK time"}
                  >
                    {subbed ? `✓ 6am daily` : "+ daily"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && active && (
        <div className="max-w-3xl mx-auto p-4 flex flex-col" style={{ height: "calc(100vh - 80px)" }}>
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto space-y-3 px-2"
            data-testid="coach-thread"
            style={{ scrollbarWidth: "thin" }}
          >
            {messages.length === 0 && (
              <div className="text-sm py-8" style={{ color: T.textDim }}>
                <div className="mb-2">{active.emoji} <strong style={{ color: T.text }}>{active.name}</strong> is ready.</div>
                <div className="italic">&ldquo;{active.tagline}&rdquo;</div>
                <div className="mt-4">Ask anything — this coach is locked in on their discipline. First message opens the session.</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className="max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap"
                  style={{
                    background: m.role === "user"
                      ? `linear-gradient(135deg, ${active.gradient?.[0] || T.cyan}, ${active.gradient?.[1] || "#8b5cf6"})`
                      : T.panel,
                    color: m.role === "user" ? T.bg : T.text,
                    border: m.role === "coach" ? `1px solid ${T.border}` : "none",
                    borderRadius: 12,
                    fontWeight: m.role === "user" ? 500 : 400,
                  }}
                  data-testid={`coach-msg-${m.role}-${i}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start" data-testid="coach-typing">
                <div className="px-4 py-2.5 text-sm italic" style={{ background: T.panel, color: T.textDim, border: `1px solid ${T.border}`, borderRadius: 12 }}>
                  {active.name} is thinking…
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="mt-4 flex gap-2" style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
            <input
              data-testid="coach-input"
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={`Ask ${active.name}…`}
              disabled={busy}
              className="flex-1 px-4 py-3 text-sm"
              style={{
                background: T.panel,
                border: `1px solid ${T.border}`,
                color: T.text,
                outline: "none",
                borderRadius: 8,
              }}
            />
            <button
              data-testid="coach-send"
              onClick={send}
              disabled={busy || !input.trim()}
              className="px-6 py-3 text-sm font-bold uppercase tracking-wider"
              style={{
                background: busy || !input.trim() ? "rgba(34,211,238,0.2)" : T.cyan,
                color: T.bg,
                border: "none",
                cursor: busy || !input.trim() ? "not-allowed" : "pointer",
                borderRadius: 8,
              }}
            >Send</button>
          </div>
        </div>
      )}
    </div>
  );
}
